/**
 * Art Style jobs on the shared creative-jobs machinery, tracked through the jobs list + `creative_job` events like
 * any creatives run, so their state survives a reload or a return visit:
 * - an extraction (kind `art_style_extract`, `root_banner_id` = the style id) reads the style, then queues its
 *   four sample draws as its children (`parent_id` = the extract job) and finishes at stage `samples_queued`, or
 *   at `samples_not_started` with the reason in `error`;
 * - a sample draw (kind `art_style_sample`, `placement` = the slot, `root_banner_id` = the style id).
 * On the casino_creation pipeline the samples are a preview batch instead (the record's `previews`): its four follower
 * jobs are sample jobs too (kind `art_style_sample`, `placement` = `preview_<n>`, `root_banner_id` = the style id,
 * `parent_id` = the batch id, NOT the extract job's), whether an extraction queued the batch or "Regenerate samples".
 * These pure helpers turn the jobs map + the record into the page's state: whether the style is being extracted,
 * which cards are waiting for their draw to start, and each card's phase.
 */
import { ART_STYLE_EXTRACT_KIND, ART_STYLE_SAMPLE_KIND, type CreativeJob } from '@/store/slices/creativeJobs';

import {
  ART_STYLE_PREVIEW_COUNT,
  ART_STYLE_SLOTS,
  previewNumberOf,
  type ArtStylePreviewItem,
  type ArtStylePreviews,
  type ArtStylePreviewsState,
  type ArtStyleSample,
  type ArtStyleSlot,
} from './art-style-api';

/** A card's phase: drawing now, the last draw failed, showing a sample, or never drawn. */
export type SlotPhase = 'generating' | 'failed' | 'ready' | 'empty';

/** Milliseconds since epoch of an RFC3339 stamp (0 when absent or unparseable). */
export const stampOf = (value: string | undefined): number => {
  const ms = value ? Date.parse(value) : NaN;
  return Number.isFinite(ms) ? ms : 0;
};

/** The samples a card shows: those drawn since the style was last extracted, since an earlier extraction's samples show
 *  a look the style no longer has. Editing the ingredients keeps them until the owner extracts again; a style with no
 *  extraction on record (or a sample with no date) shows them all. */
export const samplesSinceExtraction = (
  samples: readonly ArtStyleSample[],
  extractedAt: string | undefined,
): readonly ArtStyleSample[] => {
  const since = stampOf(extractedAt);
  if (!since) return samples;
  return samples.filter((sample) => {
    const drawn = stampOf(sample.created_at);
    return drawn === 0 || drawn >= since;
  });
};

export const isActiveJob = (job: CreativeJob | undefined): boolean =>
  job?.status === 'queued' || job?.status === 'running';

/** A job's place in time, for picking the newest. A job first seen on the socket or as the page's own optimistic
 *  row carries no `created_at` (only list reads do), so it is newer than anything a list read returned: it ranks
 *  newest until a list read stamps it. */
export const jobStamp = (job: CreativeJob): number =>
  job.created_at ? stampOf(job.created_at) : Number.POSITIVE_INFINITY;

/** Whether `job` should replace `prev` as the newest: later, or as late (two jobs only seen live, both unstamped)
 *  and not the one that ended while `prev` is in flight. The jobs map keeps the order jobs were first seen, so among
 *  unstamped jobs the later one is the one seen later: a retry, or a redraw. */
const isNewer = (job: CreativeJob, prev: CreativeJob | undefined): boolean => {
  if (!prev) return true;
  const a = jobStamp(job);
  const b = jobStamp(prev);
  return a > b || (a === b && !(isActiveJob(prev) && !isActiveJob(job)));
};

const isBrandJob = (job: CreativeJob, kind: string, tenantId: number | undefined): boolean =>
  job.kind === kind && tenantId !== undefined && String(job.tenant_id) === String(tenantId);

const isStyleJob = (job: CreativeJob, kind: string, tenantId: number | undefined, styleId: string | undefined): boolean =>
  isBrandJob(job, kind, tenantId) && styleId !== undefined && styleId !== '' && job.root_banner_id === styleId;

/** Whether a job is one of this brand's sample draws. */
export const isSampleJobFor = (job: CreativeJob, tenantId: number | undefined): boolean =>
  isBrandJob(job, ART_STYLE_SAMPLE_KIND, tenantId);

/** Whether a sample job draws for this style (the style id rides in `root_banner_id`). */
export const isSampleJobForStyle = (job: CreativeJob, tenantId: number | undefined, styleId: string | undefined): boolean =>
  isStyleJob(job, ART_STYLE_SAMPLE_KIND, tenantId, styleId);

/** Whether a job is one of this brand's style extractions. */
export const isExtractJobFor = (job: CreativeJob, tenantId: number | undefined): boolean =>
  isBrandJob(job, ART_STYLE_EXTRACT_KIND, tenantId);

/** Whether an extraction job reads this style (the style id rides in `root_banner_id`). */
export const isExtractJobForStyle = (job: CreativeJob, tenantId: number | undefined, styleId: string | undefined): boolean =>
  isStyleJob(job, ART_STYLE_EXTRACT_KIND, tenantId, styleId);

/** The newest sample job per slot for one of this brand's styles. */
export const latestSampleJobs = (
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): Partial<Record<ArtStyleSlot, CreativeJob>> => {
  const out: Partial<Record<ArtStyleSlot, CreativeJob>> = {};
  for (const job of Object.values(byId)) {
    if (!isSampleJobForStyle(job, tenantId, styleId)) continue;
    const slot = ART_STYLE_SLOTS.find((s) => s === job.placement);
    if (!slot) continue;
    if (isNewer(job, out[slot])) out[slot] = job;
  }
  return out;
};

/** The newest extraction job of one of this brand's styles: the one that says whether it is being extracted now,
 *  and how its last extraction ended. */
export const latestExtractJob = (
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): CreativeJob | undefined => {
  let out: CreativeJob | undefined;
  for (const job of Object.values(byId)) {
    if (isExtractJobForStyle(job, tenantId, styleId) && isNewer(job, out)) out = job;
  }
  return out;
};

/** The stage of a finished extraction that queued the style's four sample draws. */
export const EXTRACT_SAMPLES_QUEUED = 'samples_queued';
/** The stage of a finished extraction whose samples did not start; the reason is in the job's `error`. */
export const EXTRACT_SAMPLES_NOT_STARTED = 'samples_not_started';
/** Why an extraction's samples did not start: the balance could not cover them. */
export const EXTRACT_SAMPLES_NO_CREDITS = 'insufficient_credits';
/** Why an extraction's previews did not start (casino_creation pipeline): the style has no maturity rating yet. Also
 *  the code a preview request or a site save is refused with for such a style. */
export const ART_STYLE_RATING_NEEDED = 'art_style_rating_needed';

/** Per slot, whether its card waits for a draw that is on its way but has no job of its own yet: every slot while
 *  the extraction runs (its four samples follow it), and once it finished having queued them, each slot until one
 *  of that extraction's sample jobs (`parent_id` = the extract job) is known for it. That keeps the cards drawing
 *  between the extraction's end and its samples showing up, instead of flashing "Not drawn yet". A list read
 *  (its rows carry `created_at`, socket events never do) holds all the samples the extraction queued, so once one
 *  carried any of them, a slot it had none for was never queued and its card falls back to its own state. */
export const startingSlots = (
  extractJob: CreativeJob | undefined,
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): Record<ArtStyleSlot, boolean> => {
  const all = (on: boolean): Record<ArtStyleSlot, boolean> => ({ hero: on, promo: on, story: on, reward: on });
  if (!extractJob) return all(false);
  if (isActiveJob(extractJob)) return all(true);
  if (extractJob.status !== 'done' || extractJob.stage !== EXTRACT_SAMPLES_QUEUED) return all(false);
  const out = all(true);
  for (const job of Object.values(byId)) {
    if (!isSampleJobForStyle(job, tenantId, styleId) || job.parent_id !== extractJob.id) continue;
    if (job.created_at) return all(false);
    const slot = ART_STYLE_SLOTS.find((s) => s === job.placement);
    if (slot) out[slot] = false;
  }
  return out;
};

/** Whether the newest extraction failed after the record's last extraction landed: the failure is news. */
export const extractFailedSince = (extractJob: CreativeJob | undefined, extractedAt: string | undefined): boolean =>
  extractJob?.status === 'failed' && jobStamp(extractJob) > stampOf(extractedAt);

/** Whether the newest extraction finished after the record's last extraction: the record predates what it wrote
 *  (it was read while the job ran, or the job ended before this page saw it run), so it is due a re-read. */
export const extractLandedSince = (extractJob: CreativeJob | undefined, extractedAt: string | undefined): boolean =>
  extractJob?.status === 'done' && jobStamp(extractJob) > stampOf(extractedAt);

/** A card's phase from its newest job and its samples (newest first). A failure only shows while it is newer
 *  than the newest sample, so an old failed draw never hides a later good one (one only seen live is news). */
export const slotPhase = (job: CreativeJob | undefined, samples: readonly ArtStyleSample[]): SlotPhase => {
  if (isActiveJob(job)) return 'generating';
  if (job?.status === 'failed') {
    const newest = samples[0];
    if (!newest || jobStamp(job) > stampOf(newest.created_at)) return 'failed';
  }
  return samples.length > 0 ? 'ready' : 'empty';
};

/* ── preview batches (casino_creation pipeline) ─────────────────────────────── */

/**
 * A preview card's phase: waiting for casino-creation to start it, drawing, drawn, didn't draw (refunded on its own),
 * stopped, not confirmed (its batch may or may not have been taken: the "may still be drawing" state), or no batch yet.
 */
export type PreviewPhase = 'queued' | 'generating' | 'ready' | 'failed' | 'discarded' | 'uncertain' | 'empty';

/** One of the four preview cards. */
export interface PreviewCard {
  /** 1..4, the follower job's placement `preview_<n>`. */
  n: number;
  /** The casino-creation evergreen it draws ("" until the batch is posted). */
  evergreenName: string;
  /** The service's readable name for it ("" until the batch is posted). */
  label: string;
  /** The renderer casino-creation draws it with ("" until known). */
  effectiveRenderer: string;
  phase: PreviewPhase;
  /** The drawn preview ("" until ready). Kept while a new extraction draws over it. */
  url: string;
}

/** The preview panel's state: the batch its four cards show, and what the panel says about it. */
export interface PreviewsView {
  /** The batch the cards show ("" before the style has one). */
  batchId: string;
  /** The record holds that batch. False while only its jobs are known (a batch just asked for, here or in another tab):
   *  the record is due a re-read. */
  recorded: boolean;
  /** The batch's state on the record (null while it is not recorded, or before any batch). */
  state: ArtStylePreviewsState | null;
  outdated: boolean;
  fieldName: string;
  renderer: string;
  /** The reference images the batch sent (empty while it is not recorded); the panel does not show them. */
  references: string[];
  /** One line for a refused or uncertain batch. */
  error: string;
  /** Something is still drawing (or about to): "Regenerate samples" waits (the service refuses it meanwhile). */
  inFlight: boolean;
  /** Always four, in n order. */
  cards: PreviewCard[];
}

/** Whether a job follows one of the previews of this style's batches (its placement is `preview_<n>`). */
export const isPreviewJobForStyle = (
  job: CreativeJob,
  tenantId: number | undefined,
  styleId: string | undefined,
): boolean => isSampleJobForStyle(job, tenantId, styleId) && previewNumberOf(job.placement) !== undefined;

/** The newest preview batch the jobs feed knows for a style: its id (its jobs' `parent_id`), its newest job's place in
 *  time, and its newest job per preview. Undefined when the feed has none. */
export const latestPreviewBatchJobs = (
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): { batchId: string; stamp: number; jobs: Partial<Record<number, CreativeJob>> } | undefined => {
  let newest: CreativeJob | undefined;
  for (const job of Object.values(byId)) {
    if (isPreviewJobForStyle(job, tenantId, styleId) && job.parent_id && isNewer(job, newest)) newest = job;
  }
  const batchId = newest?.parent_id;
  if (!newest || !batchId) return undefined;
  const jobs: Partial<Record<number, CreativeJob>> = {};
  for (const job of Object.values(byId)) {
    const n = previewNumberOf(job.placement);
    if (n === undefined || job.parent_id !== batchId || !isPreviewJobForStyle(job, tenantId, styleId)) continue;
    if (isNewer(job, jobs[n])) jobs[n] = job;
  }
  return { batchId, stamp: jobStamp(newest), jobs };
};

/** The follower job of one recorded preview: by its job id, else by its batch and placement (a socket frame may be the
 *  first the page saw of it). */
const previewJobOf = (
  item: ArtStylePreviewItem,
  batchId: string,
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): CreativeJob | undefined =>
  (item.job_id ? byId[item.job_id] : undefined) ??
  Object.values(byId).find(
    (job) =>
      job.parent_id === batchId &&
      previewNumberOf(job.placement) === item.n &&
      isPreviewJobForStyle(job, tenantId, styleId),
  );

/** A preview's phase from its follower job alone (a batch the record does not hold yet). A finished job's preview is
 *  in the record the page is about to read, so it still draws until then. */
const jobPreviewPhase = (job: CreativeJob | undefined): PreviewPhase => {
  if (job?.status === 'failed') return 'failed';
  if (job?.status === 'running' || job?.status === 'done') return 'generating';
  return 'queued';
};

/** A recorded preview's phase: the record's state, moved on by its follower job when the job is ahead of it (a failed
 *  job failed the preview; a running or finished one is drawing until the record says how it ended). */
const itemPreviewPhase = (
  state: ArtStylePreviewsState,
  item: ArtStylePreviewItem | undefined,
  job: CreativeJob | undefined,
): PreviewPhase => {
  if (state === 'uncertain') return 'uncertain';
  if (state === 'refused') return 'failed';
  if (!item) return state === 'posted' ? 'failed' : 'queued';
  if (item.state === 'ready') return item.candidate_url ? 'ready' : 'failed';
  if (item.state === 'failed' || item.state === 'discarded') return item.state;
  if (job?.status === 'failed') return 'failed';
  // Until casino-creation took the batch, every preview waits (a follower runs while it sends the batch).
  if (state === 'new' || state === 'posting') return job?.status === 'done' ? 'generating' : 'queued';
  if (job?.status === 'running' || job?.status === 'done') return 'generating';
  return item.state;
};

const isDrawingPhase = (phase: PreviewPhase): boolean => phase === 'queued' || phase === 'generating';

const blankCard = (n: number, phase: PreviewPhase): PreviewCard => ({
  n,
  evergreenName: '',
  label: '',
  effectiveRenderer: '',
  phase,
  url: '',
});

/**
 * The preview panel's state from the record's newest batch and the jobs feed. The feed can be ahead of the record: a
 * batch whose jobs are newer than the record's batch (just asked for, here or in another tab) shows from its jobs
 * until the record holds it. A batch whose jobs all ended and were only seen live (never listed, so not stamped) is
 * not taken for ahead: its end re-read the record, which is then the truth. While an extraction runs (`starting`),
 * its batch follows it: every card draws, over the preview it shows now. Pure.
 */
export const previewsView = (
  previews: ArtStylePreviews | null,
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
  starting = false,
): PreviewsView => {
  const numbers = Array.from({ length: ART_STYLE_PREVIEW_COUNT }, (_, i) => i + 1);
  const ahead = latestPreviewBatchJobs(byId, tenantId, styleId);
  const isAhead =
    ahead !== undefined &&
    ahead.batchId !== previews?.batch_id &&
    (Number.isFinite(ahead.stamp) || Object.values(ahead.jobs).some(isActiveJob)) &&
    ahead.stamp > stampOf(previews?.created_at);
  let view: PreviewsView;
  if (ahead && isAhead) {
    view = {
      batchId: ahead.batchId,
      recorded: false,
      state: null,
      outdated: false,
      fieldName: previews?.field_name ?? '',
      renderer: '',
      references: [],
      error: '',
      inFlight: false,
      cards: numbers.map((n) => blankCard(n, jobPreviewPhase(ahead.jobs[n]))),
    };
  } else if (previews) {
    view = {
      batchId: previews.batch_id,
      recorded: true,
      state: previews.state,
      outdated: previews.outdated,
      fieldName: previews.field_name,
      renderer: previews.renderer,
      references: previews.references,
      error: previews.error,
      inFlight: false,
      cards: numbers.map((n) => {
        const item = previews.items.find((it) => it.n === n);
        const job = item ? previewJobOf(item, previews.batch_id, byId, tenantId, styleId) : undefined;
        const phase = itemPreviewPhase(previews.state, item, job);
        return {
          n,
          evergreenName: item?.evergreen_name ?? '',
          label: item?.label ?? '',
          effectiveRenderer: item?.effective_renderer ?? '',
          phase,
          url: phase === 'ready' && item ? item.candidate_url : '',
        };
      }),
    };
  } else {
    view = {
      batchId: '',
      recorded: false,
      state: null,
      outdated: false,
      fieldName: '',
      renderer: '',
      references: [],
      error: '',
      inFlight: false,
      cards: numbers.map((n) => blankCard(n, 'empty')),
    };
  }
  if (starting) view.cards = view.cards.map((card) => ({ ...card, phase: 'generating' }));
  const batchSending = view.state === 'new' || view.state === 'posting';
  view.inFlight = batchSending || view.cards.some((card) => isDrawingPhase(card.phase));
  return view;
};

/**
 * Whether the record is behind the jobs feed on its previews, as a key that changes with what it is behind on ("" when
 * it is not): a batch only the jobs know, or a recorded preview still drawing on the record whose follower job ended.
 * The page re-reads the record while it is. Pure.
 */
export const previewsBehindKey = (
  view: PreviewsView,
  previews: ArtStylePreviews | null,
  byId: Record<string, CreativeJob>,
  tenantId: number | undefined,
  styleId: string | undefined,
): string => {
  if (view.batchId && !view.recorded) return `batch:${view.batchId}`;
  if (!previews || previews.state === 'refused' || previews.state === 'uncertain') return '';
  const behind: string[] = [];
  for (const item of previews.items) {
    if (item.state !== 'queued' && item.state !== 'generating') continue;
    const job = previewJobOf(item, previews.batch_id, byId, tenantId, styleId);
    if (job && (job.status === 'done' || job.status === 'failed')) behind.push(`${job.id}:${job.status}`);
  }
  return behind.join('|');
};
